/**
 * LELoanSummary - Loan terms and summary section
 *
 * Displays loan amount, rate, monthly P&I, and loan details
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Percent, Calendar, Home, Lock, AlertCircle, Check, X } from 'lucide-react';
import { LESection } from './LESection';
import type { ParsedLoanEstimate } from './types';

interface LELoanSummaryProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format percentage for display
 */
function formatPercent(value: number): string {
  return `${value.toFixed(3)}%`;
}

export const LELoanSummary: React.FC<LELoanSummaryProps> = ({ data, className }) => {
  const loanDetails = [
    data.loanTerm,
    data.loanPurpose,
    data.loanProduct,
    data.loanType
  ].filter(Boolean);

  return (
    <LESection title="Loan Terms" icon={<DollarSign className="h-4 w-4" />} className={className}>
      <div className="space-y-4">
        {/* Main Loan Terms Table */}
        <div className="grid grid-cols-1 divide-y">
          {/* Loan Amount */}
          <LoanTermRow
            label="Loan Amount"
            value={formatCurrency(data.loanAmount)}
            canIncrease={data.canIncrease.loanAmount}
          />

          {/* Interest Rate */}
          <LoanTermRow
            label="Interest Rate"
            value={formatPercent(data.interestRate)}
            canIncrease={data.canIncrease.interestRate}
          />

          {/* Monthly P&I */}
          <LoanTermRow
            label="Monthly Principal & Interest"
            value={formatCurrency(data.monthlyPI)}
            canIncrease={data.canIncrease.monthlyPI}
            increaseDetails={data.canIncrease.monthlyPIDetails}
          />
        </div>

        {/* Loan Features */}
        <div className="grid grid-cols-2 gap-4 pt-2">
          <LoanFeature
            label="Prepayment Penalty"
            hasFeature={data.prepaymentPenalty}
          />
          <LoanFeature
            label="Balloon Payment"
            hasFeature={data.balloonPayment}
          />
        </div>

        {/* Loan Details Pills */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {loanDetails.map((detail, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {detail}
            </Badge>
          ))}
        </div>

        {/* Rate Lock Status */}
        {data.rateLock && (
          <div
            className={cn(
              'flex items-center justify-between p-3 rounded-lg text-sm',
              data.rateLock.locked
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            )}
          >
            <div className="flex items-center gap-2">
              <Lock
                className={cn(
                  'h-4 w-4',
                  data.rateLock.locked ? 'text-green-600' : 'text-amber-600'
                )}
              />
              <span className="font-medium">
                {data.rateLock.locked ? 'Rate Locked' : 'Rate Not Locked'}
              </span>
            </div>
            {data.rateLock.locked && data.rateLock.until && (
              <span className="text-xs text-muted-foreground">
                Until {data.rateLock.until}
              </span>
            )}
          </div>
        )}
      </div>
    </LESection>
  );
};

/**
 * Individual loan term row
 */
interface LoanTermRowProps {
  label: string;
  value: string;
  canIncrease: boolean;
  increaseDetails?: string | null;
}

const LoanTermRow: React.FC<LoanTermRowProps> = ({
  label,
  value,
  canIncrease,
  increaseDetails
}) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-lg font-semibold tabular-nums">{value}</span>
      <div className="flex items-center gap-1.5 min-w-[100px] justify-end">
        {canIncrease ? (
          <>
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-amber-600">Can increase</span>
          </>
        ) : (
          <>
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-xs text-green-600">Fixed</span>
          </>
        )}
      </div>
    </div>
  </div>
);

/**
 * Loan feature indicator (Prepayment Penalty, Balloon)
 */
interface LoanFeatureProps {
  label: string;
  hasFeature: boolean;
}

const LoanFeature: React.FC<LoanFeatureProps> = ({ label, hasFeature }) => (
  <div className="flex items-center gap-2">
    {hasFeature ? (
      <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
        <X className="h-3 w-3 text-red-600" />
      </div>
    ) : (
      <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
        <Check className="h-3 w-3 text-green-600" />
      </div>
    )}
    <span className="text-sm">
      {label}: <span className="font-medium">{hasFeature ? 'YES' : 'NO'}</span>
    </span>
  </div>
);

export default LELoanSummary;
